import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    TenantFragment,
    Tenants_Insert_Input,
    UpdateTenantMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    SuperAdminTenantCreateFragment, 
    SuperAdminTenantCreateSchema, 
    SuperAdminTenantUpdateFragment, 
    SuperAdminTenantUpdateSchema 
} from "src/components/superadmin/tenants/SuperAdminTenantSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = SuperAdminTenantCreateFragment

let initialState: FormPayloadType = {
    name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    phone: "",
    timezone: 'America/New_York'
};

export const CreateSuperAdminTenantDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().tenants;
    const { loading, errors } = useAppState().tenants;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Tenants_Insert_Input) => {
        return createItem({ ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, TenantFragment, string>
                autoOpen={false}
                formSchema={SuperAdminTenantCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateSuperAdminTenantAction = () => {
    return (
        <PageDrawerAction<TenantFragment>
            title='Add Tenant'
            action={CreateSuperAdminTenantDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: SuperAdminTenantUpdateFragment = {
    name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    phone: "",
    timezone: 'America/New_York'
};

export const UpdateSuperAdminTenantDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().tenants;
    const { currentItem, loading, errors } = useAppState().tenants;



    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: SuperAdminTenantUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<SuperAdminTenantUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateTenantMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<SuperAdminTenantUpdateFragment, TenantFragment, string>
                autoOpen={false}
                formSchema={SuperAdminTenantUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateSuperAdminTenantAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().tenants;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.tenants.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<TenantFragment>
            title='Edit Tenant'
            action={UpdateSuperAdminTenantDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditSuperAdminTenantAction = () => {
    return (
        <PageDrawerAction<TenantFragment>
            title='Edit Tenant'
            action={UpdateSuperAdminTenantDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}